import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { MultiPropertyListWithImage } from "@/components/generic/multi-property-list-image";

import type { ApiError } from "@/data-interfaces/generics";
import type { UsedTool } from "@/data-interfaces/tools";
import { useMQTTAutoRefreshPublish, useMQTTSubscribe } from "@/hooks/use-mqtt";
import { getUsedToolDetailsProperties } from "@/lib/data-remappings/used-tool-details";
import { getToolImage } from "@/lib/image-utils";
import { getBgColor, LogLevel } from "@/lib/log-level";
import {
	getAppWrappedPageUrl,
	TOOLS_PAGE_URL,
	USED_TOOL_DETAILS_PAGE_URL,
} from "@/lib/page-routes";
import { TL_USED_TOOL_URL } from "@/lib/request-paths";

export const Route = createFileRoute(
	getAppWrappedPageUrl(USED_TOOL_DETAILS_PAGE_URL),
)({
	component: UsedToolDetails,
	validateSearch: (search: UsedTool) => search,
});

function UsedToolDetails() {
	const [t, _] = useTranslation();

	const navigate = useNavigate();

	const usedToolSearchParam: UsedTool = Route.useSearch();

	const [usedTool, setUsedTool] = useState<UsedTool | ApiError>(
		usedToolSearchParam,
	);

	useMQTTSubscribe(TL_USED_TOOL_URL, setUsedTool);

	useMQTTAutoRefreshPublish(30, TL_USED_TOOL_URL, {
		used_tool_id: usedToolSearchParam.id,
	});

	return (
		<div className="text-white p-8 flex flex-col flex-1">
			<h2 className="text-2xl sm:text-3xl hover:text-[#238b8c]">
				<button
					type="button"
					onClick={() => {
						navigate({
							to: TOOLS_PAGE_URL,
							search: () => ({ selectedTab: "used" }),
						});
					}}
				>
					{"< "}
					{t("Tools")}
				</button>
			</h2>

			<MqttSuspense
				objects={usedTool}
				requestUrl={TL_USED_TOOL_URL}
				parameters={{ used_tool_id: usedToolSearchParam.id }}
				genericError="Error retriving tool"
			>
				{(usedTool) => (
					<MultiPropertyListWithImage
						multiPropertyObject={{
							properties: getUsedToolDetailsProperties(usedTool),
						}}
						imageUrl={getToolImage(usedTool.type.id)}
						badges={[
							{
								label: "Required",
								twColor: getBgColor(LogLevel.Warning),
								visible: usedTool.is_required,
							},
							{
								label: "Broken",
								twColor: getBgColor(LogLevel.Error),
								visible: usedTool.is_broken,
							},
						]}
					/>
				)}
			</MqttSuspense>
		</div>
	);
}
